/**
 * People Import Wizard — Excel-safe .xlsx / .csv round-trip for Drivers / Staff / Passengers.
 * Mirrors the Schedule Import Wizard pattern:
 *   Step 1 — Upload (drag & drop, templates)
 *   Step 2 — Review: editable grid, New vs Existing counts, Create-only / Update-existing choice
 *   Step 3 — Dry-run summary
 *   Step 4 — Result
 * Matching: Employee ID / Passenger ID first, falling back to email.
 * account_status column: a truthy value (yes/active/enabled/pending_invitation) ticks
 * "Create Account" on import — the backend then sends the activation invitation.
 */
import React, { useState, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Loader2, CheckCircle, AlertTriangle, FileSpreadsheet, ArrowRight, ArrowLeft, Download, Copy } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { useAuth } from '../../contexts/AuthContext';
import { getDateFormat, formatPlainDate } from '../../utils/timezone';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';

const TYPE_META = {
  drivers: {
    label: 'Drivers', endpoint: '/drivers', idCol: 'employee_id', idLabel: 'Employee ID', singular: 'driver', groupType: 'driver',
    columns: ['employee_id', 'full_name', 'email', 'phone', 'license_number', 'license_expiry', 'address', 'date_of_birth',
      'status', 'group', 'date_started', 'emergency_contact_name', 'emergency_contact_phone', 'notes', 'personal_beacon', 'rfid_ibutton_card', 'account_status'],
    example: ['D-1001', 'Jane Driver', 'jane.driver@example.com', '+61400000001', 'LIC12345', '2027-06-30', '1 Main St',
      '1990-05-14', 'active', '', '2024-01-15', 'John Driver', '+61400000002', 'Imported example', '', '', 'yes'],
  },
  staff: {
    label: 'Staff', endpoint: '/staff', idCol: 'employee_id', idLabel: 'Employee ID', singular: 'staff', groupType: 'staff',
    columns: ['employee_id', 'full_name', 'email', 'phone', 'department', 'role', 'driver_eligible', 'license_number',
      'license_expiry', 'address', 'date_of_birth', 'status', 'group', 'date_started', 'emergency_contact_name',
      'emergency_contact_phone', 'notes', 'personal_beacon', 'rfid_ibutton_card', 'account_status'],
    example: ['S-2001', 'Sam Staff', 'sam.staff@example.com', '+61400000003', 'Operations', 'Supervisor', 'no', '', '',
      '2 High St', '1988-02-20', 'active', '', '2023-07-01', 'Sue Staff', '+61400000004', 'Imported example', '', '', 'no'],
  },
  passengers: {
    label: 'Passengers', endpoint: '/passengers', idCol: 'passenger_id', idLabel: 'Passenger ID', singular: 'passenger', groupType: 'passenger',
    columns: ['passenger_id', 'full_name', 'email', 'phone', 'address', 'date_of_birth', 'status', 'group',
      'emergency_contact_name', 'emergency_contact_phone', 'notes', 'personal_beacon', 'rfid_ibutton_card', 'account_status'],
    example: ['P-3001', 'Pat Passenger', 'pat.p@example.com', '+61400000005', '3 Park Ave', '2001-11-02', 'active', '',
      'Paula Passenger', '+61400000006', 'Imported example', '', '', 'no'],
  },
};
const TEXT_COLS = new Set(['phone', 'emergency_contact_phone', 'employee_id', 'passenger_id']);
const DATE_COLS = new Set(['license_expiry', 'date_of_birth', 'date_started']);
const STATUSES = new Set(['active', 'inactive', 'suspended']);
// Human-readable grid/table headers.
const COL_LABELS = {
  employee_id: 'Employee ID', passenger_id: 'Passenger ID', full_name: 'Full Name', email: 'Email',
  phone: 'Phone', department: 'Department', role: 'Role', driver_eligible: 'Driver Eligible',
  license_number: 'License Number', license_expiry: 'License Expiry', address: 'Address',
  date_of_birth: 'Date of Birth', status: 'Status', date_started: 'Date Started',
  emergency_contact_name: 'Emergency Contact Name', emergency_contact_phone: 'Emergency Contact Phone',
  notes: 'Notes', account_status: 'Account Status', group: 'Group',
  personal_beacon: 'Personal Beacon', rfid_ibutton_card: 'RFID / iButton Card',
};
// Beacon / RFID round-trip columns — matched to existing assets, never created.
const ASSET_COLS = new Set(['personal_beacon', 'rfid_ibutton_card']);
// "Name [IMEI]" / "Label [VALUE]" export format → { full, token (bracketed id), name }.
const parseAssetCell = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return null;
  const m = s.match(/\[([^\]]+)\]\s*$/);
  return { full: s, token: m ? m[1].trim() : '', name: m ? s.slice(0, m.index).trim() : s };
};
const normAsset = (v) => String(v || '').replace(/\s+/g, '').toUpperCase();
// Dropdown labels — same format the exports write, so the matcher resolves them.
const beaconOptionLabel = (b) => {
  const name = b.asset_name || b.name || b.imei || '';
  const imei = String(b.imei || '').trim();
  return imei && imei !== name ? `${name} [${imei}]` : name;
};
const cardOptionLabel = (c) => (c.label && c.card_value && c.label !== c.card_value ? `${c.label} [${c.card_value}]` : (c.card_value || c.label || ''));
// Export-side filename sanitiser — import must look photos up with the same key.
const photoStem = (s) => String(s || '').trim().replace(/[\\/:*?"<>|]/g, '-').toLowerCase();

// Grid dropdown for beacon / RFID cells — type-to-search filter on top.
const AssetSelect = ({ value, onChange, options, disabled, testid }) => {
  const [q, setQ] = useState('');
  const base = (!value || options.includes(value)) ? options : [value, ...options];
  const list = q ? base.filter((o) => o.toLowerCase().includes(q.toLowerCase())) : base;
  return (
    <Select value={value || '__empty__'} onValueChange={(v) => { onChange(v === '__empty__' ? '' : v); setQ(''); }} disabled={disabled}>
      <SelectTrigger className="h-7 text-xs" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        <div className="sticky top-0 z-10 bg-popover p-1 border-b">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Type to search…"
            className="h-7 text-xs"
            data-testid={`${testid}-search`}
          />
        </div>
        <SelectItem value="__empty__" className="text-muted-foreground">—</SelectItem>
        {list.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
        {list.length === 0 && <p className="px-2 py-1.5 text-xs text-muted-foreground">No matches</p>}
      </SelectContent>
    </Select>
  );
};
// Preset dropdown options (match the Add/Edit person forms).
const SELECT_COLS = {
  department: ['operations', 'sales', 'service', 'safety & compliance', 'finance', 'admin', 'management', 'technical', 'other'],
  role: ['manager', 'supervisor', 'coordinator', 'fleet manager', 'driver', 'sales person', 'safety officer', 'finance officer', 'fitter / installer', 'dispatcher', 'workshop manager', 'auditor', 'analyst', 'technician', 'other'],
  driver_eligible: ['yes', 'no'],
  status: ['active', 'inactive', 'suspended'],
};
const titleCase = (s) => String(s).replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1));
const truthy = (v) => /^(y|yes|true|1|active|enabled|pending_invitation|pending)$/i.test(String(v || '').trim());

// Parse unknown-format date → ISO YYYY-MM-DD ('' if blank, null if invalid).
function parseDateIn(raw, dateFormat = 'DD/MM/YYYY') {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const parts = s.split(/[/\-.]/).map((x) => x.trim());
  if (parts.length !== 3 || parts.some((p) => !/^\d+$/.test(p))) return null;
  let y, mo, d;
  if (parts[0].length === 4) { [y, mo, d] = parts; }
  else if (dateFormat === 'MM/DD/YYYY') { [mo, d, y] = parts; }
  else { [d, mo, y] = parts; }
  y = String(y); mo = String(mo).padStart(2, '0'); d = String(d).padStart(2, '0');
  if (y.length === 2) y = `20${y}`;
  const iso = `${y}-${mo}-${d}`;
  return (/^\d{4}-\d{2}-\d{2}$/.test(iso) && !isNaN(Date.parse(iso))) ? iso : null;
}

function buildTemplateRows(meta) {
  // Template example dates shown in the user's configured date format.
  const example = meta.columns.map((c, i) => (DATE_COLS.has(c) ? formatPlainDate(meta.example[i]) : meta.example[i]));
  return [meta.columns, example];
}
function buildTemplateSheet(meta) {
  const ws = XLSX.utils.aoa_to_sheet(buildTemplateRows(meta));
  meta.columns.forEach((h, c) => {
    if (!TEXT_COLS.has(h)) return;
    const addr = XLSX.utils.encode_cell({ r: 1, c });
    if (ws[addr]) { ws[addr].t = 's'; ws[addr].z = '@'; ws[addr].v = String(ws[addr].v); }
  });
  ws['!cols'] = meta.columns.map((h) => ({ wch: Math.max(12, h.length + 2) }));
  return ws;
}
export function downloadPeopleTemplateXlsx(personType) {
  const meta = TYPE_META[personType];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, buildTemplateSheet(meta), meta.label);
  XLSX.writeFile(wb, `${personType}_import_template.xlsx`);
}
export function downloadPeopleTemplateCsv(personType) {
  const meta = TYPE_META[personType];
  const csv = buildTemplateRows(meta).map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\r\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
  const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `${personType}_import_template.csv`; a.click();
}

const StepDot = ({ n, step, label }) => (
  <div className="flex items-center gap-2">
    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${step === n ? 'bg-[#2563eb] text-white' : step > n ? 'bg-emerald-500 text-white' : 'bg-muted text-muted-foreground'}`}>
      {step > n ? <CheckCircle className="w-3.5 h-3.5" /> : n}
    </div>
    <span className={`text-xs ${step === n ? 'font-semibold' : 'text-muted-foreground'}`}>{label}</span>
  </div>
);

export default function PeopleImportWizard({ open, onClose, api, personType = 'drivers', existingPeople = [], onImported, groups = [] }) {
  const meta = TYPE_META[personType] || TYPE_META.drivers;
  // Dates are read & validated in the user's configured date format (General Settings).
  const dateFormat = getDateFormat() || 'DD/MM/YYYY';
  const groupNames = useMemo(() => groups.map((g) => g.name), [groups]);
  const groupIdByName = useMemo(() => Object.fromEntries(groups.map((g) => [String(g.name).trim().toLowerCase(), g.id])), [groups]);
  // Photos extracted from a Full Backup .zip, keyed by lowercase ID/filename stem.
  const photosRef = useRef({});
  const backupRef = useRef({ manifest: null, fileMap: {} });
  const [step, setStep] = useState(1);
  const [rows, setRows] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [results, setResults] = useState(null);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [duplicateMode, setDuplicateMode] = useState('skip'); // 'skip' | 'update'
  // Always fetch the FULL list on open — the parent's state may be a paginated/filtered page.
  const [serverPeople, setServerPeople] = useState(null);
  // Resource limit check — warn in the dry-run BEFORE creates start failing.
  const { user } = useAuth();
  const [limitInfo, setLimitInfo] = useState(null); // { current, limit } for this person type
  // Beacon / RFID inventories for round-trip assignment matching.
  const [beacons, setBeacons] = useState([]);
  const [rfidCards, setRfidCards] = useState([]);
  const fileInputRef = useRef(null);

  React.useEffect(() => {
    if (!open) { setServerPeople(null); setLimitInfo(null); return; }
    let cancelled = false;
    api.get(meta.endpoint).then((res) => { if (!cancelled) setServerPeople(res.data || []); }).catch(() => {});
    api.get('/people/personal-beacons').then((res) => { if (!cancelled) setBeacons(res.data || []); }).catch(() => {});
    api.get('/people/rfid-ibutton-cards').then((res) => { if (!cancelled) setRfidCards(res.data || []); }).catch(() => {});
    if (user?.id) {
      api.get(`/users/${user.id}/resource-usage`).then((res) => {
        if (cancelled) return;
        const limit = res.data?.limits?.[personType];
        const current = res.data?.current?.[personType];
        if (typeof limit === 'number') setLimitInfo({ limit, current: Number(current) || 0 });
      }).catch(() => {});
    }
    return () => { cancelled = true; };
  }, [open, meta.endpoint, api, user?.id, personType]);
  const allPeople = serverPeople || existingPeople;

  const existingById = useMemo(() => {
    const m = {};
    allPeople.forEach((p) => {
      const key = String(p[meta.idCol] || '').trim().toLowerCase();
      if (key) m[key] = p;
    });
    return m;
  }, [allPeople, meta.idCol]);
  const existingByEmail = useMemo(() => {
    const m = {};
    allPeople.forEach((p) => { const e = String(p.email || '').trim().toLowerCase(); if (e) m[e] = p; });
    return m;
  }, [allPeople]);

  const findExisting = useCallback((data) => {
    const idv = String(data[meta.idCol] || '').trim().toLowerCase();
    if (idv && existingById[idv]) return existingById[idv];
    const em = String(data.email || '').trim().toLowerCase();
    if (em && existingByEmail[em]) return existingByEmail[em];
    return null;
  }, [existingById, existingByEmail, meta.idCol]);

  const validate = useCallback((data) => {
    const errors = [];
    const req = (field) => errors.push(`${COL_LABELS[field] || field} is required`);
    if (!String(data.full_name || '').trim()) req('full_name');
    // email / phone / department / role are optional — the backend accepts them empty and
    // real records exist without contact details, so backups must round-trip them.
    if (personType === 'drivers') {
      if (!String(data.license_number || '').trim()) req('license_number');
      if (!String(data.license_expiry || '').trim()) req('license_expiry');
    } else if (personType === 'staff') {
      if (truthy(data.driver_eligible) && (!String(data.license_number || '').trim() || !String(data.license_expiry || '').trim())) {
        errors.push('License Number + License Expiry required when Driver Eligible is yes');
      }
    }
    if (data.status && !STATUSES.has(String(data.status).trim().toLowerCase())) errors.push('Status must be Active / Inactive / Suspended');
    DATE_COLS.forEach((c) => {
      if (meta.columns.includes(c) && data[c] && parseDateIn(data[c], dateFormat) === null) errors.push(`${COL_LABELS[c]} is not a valid date (${dateFormat})`);
    });
    if (truthy(data.account_status) && !String(data.email || '').trim() && !String(data.phone || '').trim()) {
      errors.push('Account Status needs an email or phone to deliver the invitation');
    }
    if (data.group && !groupIdByName[String(data.group).trim().toLowerCase()]) {
      errors.push(`Group "${data.group}" doesn't exist — pick one from the dropdown or clear it`);
    }
    const existing = findExisting(data);
    return { errors, existing };
  }, [personType, findExisting, dateFormat, meta.columns, groupIdByName]);

  // Unassigned assets offered in the grid dropdowns.
  const beaconOptions = useMemo(() => [...new Set(beacons.filter((b) => !b.personal_beacon_assigned_to).map(beaconOptionLabel).filter(Boolean))], [beacons]);
  const cardOptions = useMemo(() => [...new Set(rfidCards.filter((c) => !c.assigned_person_id).map(cardOptionLabel).filter(Boolean))], [rfidCards]);

  const enriched = useMemo(() => {
    const claimedBeacons = new Set();
    const claimedCards = new Set();
    const singular = meta.groupType; // 'driver' | 'staff' | 'passenger'
    const resolveBeacon = (raw, existing) => {
      const cell = parseAssetCell(raw);
      if (!cell) return null;
      const b = beacons.find((x) =>
        (x.imei && (String(x.imei).trim() === cell.token || normAsset(x.imei) === normAsset(cell.full)))
        || [x.asset_name, x.name].some((n) => n && String(n).trim().toLowerCase() === cell.name.toLowerCase()));
      if (!b) return { status: 'unmatched' };
      const a = b.personal_beacon_assigned_to;
      if (a && existing && a.type === singular && a.id === existing.id) return { status: 'self', item: b };
      if (a) return { status: 'taken', item: b, holder: a.name || 'another person' };
      if (claimedBeacons.has(b.id)) return { status: 'duplicate', item: b };
      claimedBeacons.add(b.id);
      return { status: 'assign', item: b };
    };
    const resolveCard = (raw, existing) => {
      const cell = parseAssetCell(raw);
      if (!cell) return null;
      const c = rfidCards.find((x) =>
        (x.card_value && (normAsset(x.card_value) === normAsset(cell.token) || normAsset(x.card_value) === normAsset(cell.full)))
        || (x.label && String(x.label).trim().toLowerCase() === cell.name.toLowerCase()));
      if (!c) return { status: 'unmatched' };
      if (c.assigned_person_id && existing && c.assigned_person_type === singular && c.assigned_person_id === existing.id) return { status: 'self', item: c };
      if (c.assigned_person_id) return { status: 'taken', item: c, holder: c.assigned_person_name || 'another person' };
      if (claimedCards.has(c.id)) return { status: 'duplicate', item: c };
      claimedCards.add(c.id);
      return { status: 'assign', item: c };
    };
    return rows.map((r) => {
      const v = validate(r.data);
      const isMatch = !!v.existing;
      const canSelect = v.errors.length === 0 && (!isMatch || duplicateMode === 'update');
      const beaconRes = resolveBeacon(r.data.personal_beacon, v.existing);
      const cardRes = resolveCard(r.data.rfid_ibutton_card, v.existing);
      return { ...r, v, isMatch, canSelect, beaconRes, cardRes };
    });
  }, [rows, validate, duplicateMode, beacons, rfidCards, meta.groupType]);

  const counts = useMemo(() => {
    let create = 0, update = 0, dupSkipped = 0, invalid = 0, accounts = 0, beaconAssigns = 0, cardAssigns = 0;
    enriched.forEach((r) => {
      if (r.v.errors.length) { invalid++; return; }
      if (r.isMatch) {
        if (duplicateMode === 'update' && r.selected) update++;
        else { dupSkipped++; return; }
      } else if (r.selected) create++;
      else return;
      if (truthy(r.data.account_status)) accounts++;
      if (r.beaconRes?.status === 'assign') beaconAssigns++;
      if (r.cardRes?.status === 'assign') cardAssigns++;
    });
    return { create, update, dupSkipped, invalid, accounts, beaconAssigns, cardAssigns, total: enriched.length, ready: create + update };
  }, [enriched, duplicateMode]);

  const reset = () => { setStep(1); setRows([]); setResults(null); setFileName(''); setDragOver(false); setDuplicateMode('skip'); photosRef.current = {}; backupRef.current = { manifest: null, fileMap: {} }; };
  const close = () => { reset(); onClose(); };

  const parseFile = (file) => {
    if (!file) return;
    setParsing(true);
    photosRef.current = {};
    const isXlsx = /\.xlsx$/i.test(file.name);
    const isZip = /\.zip$/i.test(file.name);

    const parseCsvText = (text) => text.replace(/^\uFEFF/, '').split(/\r?\n/).filter((l) => l.trim() !== '').map((line) => {
      const out = []; let cur = '', q = false;
      for (let i = 0; i < line.length; i++) { const ch = line[i];
        if (q) { if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; } else if (ch === '"') q = false; else cur += ch; }
        else { if (ch === '"') q = true; else if (ch === ',') { out.push(cur); cur = ''; } else cur += ch; } }
      out.push(cur); return out;
    });
    const parseXlsxBuffer = (buf) => {
      const wb = XLSX.read(buf, { type: 'array', cellText: true, raw: false });
      return XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: '' });
    };

    const ingestAoa = (aoa) => {
      if (!aoa || aoa.length < 2) { toast.error('File has no data rows'); return false; }
      const header = aoa[0].map((h) => String(h || '').trim().toLowerCase());
      const idx = {}; meta.columns.forEach((c) => { idx[c] = header.findIndex((h) => h === c); });
      if (idx.full_name === -1) { toast.error('Missing required column "full_name". Use the template.'); return false; }
      const parsed = aoa.slice(1).map((r) => {
        const data = {};
        meta.columns.forEach((c) => {
          // Strip the Excel-safe ="..." wrapper some exports use for phone columns.
          let val = idx[c] >= 0 ? String(r[idx[c]] ?? '').trim() : '';
          const m = val.match(/^="?(.*?)"?$/);
          if (m) val = m[1];
          data[c] = val;
        });
        // Normalise dropdown columns so the selects default to the sheet's value.
        if (meta.columns.includes('driver_eligible')) {
          data.driver_eligible = data.driver_eligible === '' ? '' : (truthy(data.driver_eligible) ? 'yes' : 'no');
        }
        ['status', 'department', 'role'].forEach((c) => {
          if (data[c] && SELECT_COLS[c]) {
            const lc = data[c].trim().toLowerCase();
            if (SELECT_COLS[c].includes(lc)) data[c] = lc;
          }
        });
        if (data.group) {
          const match = groupNames.find((n) => n.trim().toLowerCase() === data.group.trim().toLowerCase());
          if (match) data.group = match;
        }
        return { data };
      }).filter((r) => Object.values(r.data).some((v) => v !== ''));
      const withSel = parsed.map((r) => {
        const v = validate(r.data);
        return { ...r, selected: v.errors.length === 0 && !v.existing };
      });
      setRows(withSel);
      setFileName(file.name);
      setStep(2);
      return true;
    };

    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        if (isZip) {
          // Full Backup zip: sheet + photos/<stem>.<ext> + attachments/<stem>/… + manifest.json (v2).
          const zip = await JSZip.loadAsync(ev.target.result);
          let aoa = null;
          let manifest = null;
          const fileMap = {};
          const photoEntries = [];
          for (const [path, entry] of Object.entries(zip.files)) {
            if (entry.dir || path.startsWith('__MACOSX')) continue;
            const base = path.split('/').pop();
            fileMap[path] = entry;
            if (!aoa && /\.xlsx$/i.test(base)) aoa = parseXlsxBuffer(await entry.async('array'));
            else if (!aoa && /\.csv$/i.test(base)) aoa = parseCsvText(await entry.async('string'));
            else if (/^manifest\.json$/i.test(base)) { try { manifest = JSON.parse(await entry.async('string')); } catch { /* bad manifest — fall back to stems */ } }
            else if (/\.(jpe?g|png|webp|gif)$/i.test(base) && !/(^|\/)attachments\//i.test(path)) photoEntries.push([base, entry]);
          }
          if (!aoa) { toast.error('No .xlsx or .csv sheet found inside the zip'); return; }
          for (const [base, entry] of photoEntries) {
            const stem = base.replace(/\.[^.]+$/, '').trim().toLowerCase();
            photosRef.current[stem] = { blob: await entry.async('blob'), name: base };
          }
          backupRef.current = { manifest, fileMap };
          const docTotal = (manifest?.people || []).reduce((s, e) => s + (e.attachments?.length || 0), 0);
          if (ingestAoa(aoa) && (photoEntries.length || docTotal)) toast.info(`${photoEntries.length} photo(s) and ${docTotal} document(s) found in backup — they'll be restored on import`);
        } else if (isXlsx) {
          ingestAoa(parseXlsxBuffer(ev.target.result));
        } else {
          ingestAoa(parseCsvText(new TextDecoder('utf-8').decode(new Uint8Array(ev.target.result))));
        }
      } catch (err) {
        console.error('People import parse failed:', err);
        toast.error('Failed to parse file');
      } finally { setParsing(false); }
    };
    reader.readAsArrayBuffer(file);
  };

  const setCell = (i, col, val) => setRows((prev) => prev.map((r, j) => j === i ? { ...r, data: { ...r.data, [col]: val } } : r));
  const toggleSelected = (i) => setRows((prev) => prev.map((r, j) => j === i ? { ...r, selected: !r.selected } : r));
  const toggleSelectAll = (checked) => setRows((prev) => prev.map((r) => {
    const v = validate(r.data);
    const canSel = v.errors.length === 0 && (!v.existing || duplicateMode === 'update');
    return { ...r, selected: !!checked && canSel };
  }));

  const buildPayload = (data, forUpdate) => {
    const singularType = personType === 'passengers' ? 'passenger' : personType === 'staff' ? 'staff' : 'driver';
    const p = {};
    const put = (k, v) => { if (!forUpdate || (v !== '' && v != null)) p[k] = v; };
    meta.columns.forEach((c) => {
      if (c === 'account_status' || c === 'group' || ASSET_COLS.has(c)) return;
      let v = data[c];
      if (DATE_COLS.has(c)) v = parseDateIn(v, dateFormat) || '';
      if (c === 'status') v = String(v || '').trim().toLowerCase() || (forUpdate ? '' : 'active');
      if (c === 'driver_eligible') {
        if (v === '' && forUpdate) return; // blank cell must not flip an existing flag
        p.driver_eligible = truthy(v);
        return;
      }
      put(c, v);
    });
    if (!forUpdate) {
      if (singularType === 'driver' || singularType === 'staff') { p.email = p.email || ''; p.phone = p.phone || ''; }
    }
    if (truthy(data.account_status)) {
      p.account_enabled = true;
      p.invite_channels = String(data.email || '').trim() ? ['email'] : ['sms'];
    }
    return p;
  };

  const runImport = async () => {
    setApplying(true); setStep(4);
    let created = 0, updated = 0, invited = 0, grouped = 0, photos = 0, docs = 0, beaconsAssigned = 0, cardsAssigned = 0;
    // Deduped-stem claiming: two people exporting the same name/ID get "name", "name-2" … in the zip.
    const manifestMap = {};
    (backupRef.current.manifest?.people || []).forEach((e) => { if (e.key) manifestMap[String(e.key).toLowerCase()] = e; });
    const claimedStems = new Set();
    const claimStem = (base) => {
      if (!base) return null;
      for (const c of [base, ...Array.from({ length: 8 }, (_, k) => `${base}-${k + 2}`)]) {
        if (claimedStems.has(c)) continue;
        if (photosRef.current[c] || manifestMap[c]) { claimedStems.add(c); return c; }
      }
      return null;
    };
    const failed = [];
    const selected = enriched.filter((r) => r.selected && r.canSelect);
    for (const r of selected) {
      try {
        let personId;
        if (r.isMatch && duplicateMode === 'update') {
          const payload = buildPayload(r.data, true);
          if (r.v.existing.account_enabled) { delete payload.account_enabled; delete payload.invite_channels; }
          await api.put(`${meta.endpoint}/${r.v.existing.id}`, payload);
          personId = r.v.existing.id;
          updated++;
          if (payload.account_enabled) invited++;
        } else {
          const payload = buildPayload(r.data, false);
          const res = await api.post(meta.endpoint, payload);
          personId = res.data?.id;
          created++;
          if (payload.account_enabled) invited++;
        }
        // Group assignment (per-type people groups, matched by name).
        if (personId && r.data.group) {
          const gid = groupIdByName[String(r.data.group).trim().toLowerCase()];
          if (gid) {
            try { await api.post(`/people-groups/${gid}/add-items`, { item_ids: [personId] }); grouped++; }
            catch { failed.push({ name: r.data.full_name, error: `imported, but group "${r.data.group}" assignment failed` }); }
          }
        }
        // Beacon / RFID card assignment (matched to existing free assets only).
        if (personId) {
          const assign = {};
          if (r.beaconRes?.status === 'assign') assign.personal_beacon_id = r.beaconRes.item.id;
          if (r.cardRes?.status === 'assign') assign.rfid_ibutton_card_id = r.cardRes.item.id;
          if (Object.keys(assign).length) {
            try {
              await api.put(`${meta.endpoint}/${personId}`, assign);
              if (assign.personal_beacon_id) beaconsAssigned++;
              if (assign.rfid_ibutton_card_id) cardsAssigned++;
            } catch (e2) {
              failed.push({ name: r.data.full_name, error: e2?.response?.data?.detail || 'imported, but the beacon/RFID assignment failed' });
            }
          }
        }
        // Photo + document restore from a Full Backup zip, matched by ID column (fallback: full name).
        if (personId) {
          const stem = claimStem(photoStem(r.data[meta.idCol]) || photoStem(r.data.full_name));
          const photo = stem && photosRef.current[stem];
          if (photo) {
            try {
              const fd = new FormData();
              fd.append('file', new File([photo.blob], photo.name, { type: photo.blob.type || 'image/jpeg' }));
              await api.post(`/people/${meta.singular}/${personId}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              photos++;
            } catch { failed.push({ name: r.data.full_name, error: 'imported, but the photo upload failed' }); }
          }
          for (const att of (stem && manifestMap[stem]?.attachments) || []) {
            const zEntry = backupRef.current.fileMap[att.file];
            if (!zEntry) continue;
            if (r.isMatch && (r.v.existing.attachments || []).some((a) => a.name === att.name)) continue; // already on the person
            try {
              const blob = await zEntry.async('blob');
              const fd = new FormData();
              fd.append('file', new File([blob], att.name || 'document', { type: att.content_type || blob.type || 'application/octet-stream' }));
              const up = await api.post(`/people/${meta.singular}/${personId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
              const fid = up.data?.attachment?.id;
              if (fid && (att.expiry_date || (att.doc_type && att.doc_type !== 'other') || att.note)) {
                await api.patch(`/people/${meta.singular}/${personId}/attachments/${fid}`, { expiry_date: att.expiry_date || null, doc_type: att.doc_type || 'other', note: att.note || null });
              }
              docs++;
            } catch { failed.push({ name: r.data.full_name, error: `imported, but document "${att.name}" failed to restore` }); }
          }
        }
      } catch (e) {
        failed.push({ name: r.data.full_name, error: e?.response?.data?.detail || 'Failed' });
      }
    }
    const photosInZip = Object.keys(photosRef.current).length;
    if (photosInZip && photos === 0 && (created || updated)) {
      failed.push({ name: 'Photos', error: `${photosInZip} photo(s) in the backup could not be matched to the imported rows (check the ID / name columns weren't changed)` });
    }
    setResults({ created, updated, invited, grouped, photos, docs, beaconsAssigned, cardsAssigned, failed });
    setApplying(false);
    if (created || updated) {
      toast.success(`Imported — ${created} created, ${updated} updated${invited ? `, ${invited} invitation(s) sent` : ''}${photos ? `, ${photos} photo(s)` : ''}${docs ? `, ${docs} document(s)` : ''}${beaconsAssigned ? `, ${beaconsAssigned} beacon(s)` : ''}${cardsAssigned ? `, ${cardsAssigned} card(s)` : ''}`);
      onImported && onImported();
    }
  };

  const selectable = enriched.filter((r) => r.canSelect);
  const allSelected = selectable.length > 0 && selectable.every((r) => r.selected);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="w-[97vw] max-w-6xl h-[90vh] flex flex-col p-0" data-testid="people-import-wizard">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Import {meta.label}</DialogTitle>
          <DialogDescription>Upload an .xlsx or .csv, review & fix rows, then import. Matches existing {meta.label.toLowerCase()} by {meta.idLabel} (or email) so you can choose to skip or update them.</DialogDescription>
          <div className="flex items-center gap-4 pt-2">
            <StepDot n={1} step={step} label="Upload" /><ArrowRight className="w-3 h-3 text-muted-foreground" />
            <StepDot n={2} step={step} label="Review" /><ArrowRight className="w-3 h-3 text-muted-foreground" />
            <StepDot n={3} step={step} label="Dry-run" /><ArrowRight className="w-3 h-3 text-muted-foreground" />
            <StepDot n={4} step={step} label="Result" />
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-2">
          {step === 1 && (
            <div className="space-y-4 py-2" data-testid="people-import-step1">
              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? 'border-blue-400 bg-blue-50/60 dark:border-blue-700 dark:bg-blue-950/20' : fileName ? 'border-green-300 dark:border-green-800 bg-green-50/40 dark:bg-green-950/10' : 'border-border'}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer?.files?.[0]; if (!f) return; if (!/\.(csv|xlsx|zip)$/i.test(f.name)) { toast.error('Only .csv, .xlsx and .zip files are supported'); return; } parseFile(f); }}
                data-testid="people-import-dropzone"
              >
                {parsing ? (
                  <><Loader2 className="w-10 h-10 mx-auto animate-spin text-muted-foreground mb-3" /><p className="text-sm text-muted-foreground">Parsing…</p></>
                ) : (
                  <>
                    <Download className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                    <p className="text-sm text-muted-foreground mb-4">Drag &amp; drop a {meta.label} CSV, Excel or Full Backup (.zip) file here, or select one (exported from EnsueLIVETRAK or matching the template column names). Backup zips restore photos too.</p>
                  </>
                )}
                <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.zip,text/csv,application/zip,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" data-testid="people-import-file-input" onChange={(e) => { parseFile(e.target.files?.[0]); e.target.value = ''; }} />
                <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="people-import-select-file-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" /> Select CSV / Excel / Backup zip
                </Button>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  <span className="font-semibold text-foreground">Templates:</span>
                  <Button variant="link" size="sm" className="text-xs h-auto py-0 text-muted-foreground hover:text-foreground" onClick={() => downloadPeopleTemplateXlsx(personType)} data-testid="people-import-template-xlsx"><Download className="w-3 h-3 mr-1" /> Excel-safe (.xlsx)</Button>
                  <span>·</span>
                  <Button variant="link" size="sm" className="text-xs h-auto py-0 text-muted-foreground hover:text-foreground" onClick={() => downloadPeopleTemplateCsv(personType)} data-testid="people-import-template-csv"><Download className="w-3 h-3 mr-1" /> CSV (.csv)</Button>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground text-center">
                Set <b>account_status</b> to <b>yes</b> to create a login and send the activation invitation (email first, SMS if no email).
                The <b>group</b> column assigns the person to one of your {meta.label} groups (must match an existing group name).
                <b> personal_beacon</b> and <b>rfid_ibutton_card</b> round-trip too — values are matched to your existing beacons (IMEI or name) and cards (card value or label). Matched &amp; free assets are assigned on import; anything in use or unmatched is skipped with a note (new assets are never created).
                Full Backup zips also restore profile photos and document attachments (with their expiry dates, types and notes).
              </p>
            </div>
          )}

          {(step === 2 || step === 3) && (
            <div className="space-y-3">
              {step === 2 && (
                <div className="rounded-lg border p-3 bg-background/60" data-testid="people-import-mode">
                  <p className="text-xs font-semibold mb-1">Existing {meta.label.toLowerCase()} (matched by {meta.idLabel} or email)</p>
                  <RadioGroup value={duplicateMode} onValueChange={setDuplicateMode} className="flex gap-4">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer"><RadioGroupItem value="skip" data-testid="people-import-mode-skip" /> Create only (skip matches)</label>
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer"><RadioGroupItem value="update" data-testid="people-import-mode-update" /> Update existing (only filled cells)</label>
                  </RadioGroup>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs flex-wrap" data-testid="people-import-counts">
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{counts.create} new</Badge>
                {duplicateMode === 'update' && <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{counts.update} to update</Badge>}
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{counts.dupSkipped} existing skipped</Badge>
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">{counts.invalid} with errors</Badge>
                {counts.accounts > 0 && <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">{counts.accounts} account invite(s)</Badge>}
                <span className="text-muted-foreground ml-auto">{counts.total} rows</span>
              </div>

              <div className="border rounded-md overflow-auto max-h-[52vh]" data-testid="people-import-grid">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0 z-10">
                    <tr>
                      <th className="p-1.5 text-left w-8">
                        <Checkbox checked={allSelected} onCheckedChange={toggleSelectAll} disabled={step === 3 || selectable.length === 0} data-testid="people-import-selectall" />
                      </th>
                      <th className="p-1.5 text-left w-8">#</th>
                      {meta.columns.map((c) => <th key={c} className="p-1.5 text-left font-medium whitespace-nowrap">{COL_LABELS[c] || titleCase(c.replace(/_/g, ' '))}</th>)}
                      <th className="p-1.5 text-left font-medium whitespace-nowrap">Import Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enriched.map((r, i) => {
                      const bad = r.v.errors.length > 0;
                      return (
                        <tr key={i} className={`border-t ${bad ? 'bg-red-50/60 dark:bg-red-950/20' : r.isMatch ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''}`} data-testid={`people-import-row-${i}`}>
                          <td className="p-1 align-top pt-1.5"><Checkbox checked={!!r.selected} disabled={step === 3 || !r.canSelect} onCheckedChange={() => toggleSelected(i)} data-testid={`people-import-select-${i}`} /></td>
                          <td className="p-1 align-top pt-2 text-muted-foreground">{i + 1}</td>
                          {meta.columns.map((c) => {
                            const selOpts = c === 'group' ? groupNames : SELECT_COLS[c];
                            if (ASSET_COLS.has(c)) {
                              const res = c === 'personal_beacon' ? r.beaconRes : r.cardRes;
                              const note = !res ? null
                                : res.status === 'assign' ? { cls: 'text-emerald-600', text: 'will assign' }
                                : res.status === 'self' ? { cls: 'text-muted-foreground', text: 'already assigned' }
                                : res.status === 'taken' ? { cls: 'text-amber-600', text: `in use by ${res.holder} — skipped` }
                                : res.status === 'duplicate' ? { cls: 'text-amber-600', text: 'used by an earlier row — skipped' }
                                : { cls: 'text-amber-600', text: 'no match — skipped' };
                              return (
                                <td key={c} className="p-0.5 align-top">
                                  <div className="min-w-[160px]">
                                    <AssetSelect
                                      value={r.data[c]}
                                      onChange={(v) => setCell(i, c, v)}
                                      options={c === 'personal_beacon' ? beaconOptions : cardOptions}
                                      disabled={step === 3}
                                      testid={`people-import-${c}-${i}`}
                                    />
                                    {note && <p className={`text-[10px] leading-tight mt-0.5 ${note.cls}`} data-testid={`people-import-${c}-status-${i}`}>{note.text}</p>}
                                  </div>
                                </td>
                              );
                            }
                            return (
                            <td key={c} className="p-0.5 align-top">
                              {selOpts ? (
                                <Select
                                  value={r.data[c] || '__empty__'}
                                  onValueChange={(v) => setCell(i, c, v === '__empty__' ? '' : v)}
                                  disabled={step === 3}
                                >
                                  <SelectTrigger className="h-7 text-xs min-w-[130px]" data-testid={`people-import-select-${c}-${i}`}>
                                    <SelectValue placeholder="—" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__empty__" className="text-muted-foreground">—</SelectItem>
                                    {(selOpts.includes(r.data[c]) || !r.data[c] ? selOpts : [r.data[c], ...selOpts]).map((opt) => (
                                      <SelectItem key={opt} value={opt}>{c === 'group' ? opt : titleCase(opt)}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input value={r.data[c]} onChange={(e) => setCell(i, c, e.target.value)} disabled={step === 3}
                                  className={`h-7 text-xs ${c === 'full_name' || c === 'email' || c === 'address' ? 'min-w-[170px]' : c === 'notes' ? 'min-w-[140px]' : 'min-w-[110px]'}`} />
                              )}
                            </td>
                            );
                          })}
                          <td className="p-1 align-top pt-2 min-w-[180px] max-w-[260px]">
                            {bad ? (
                              <span className="text-red-600 flex items-start gap-1 text-[11px] leading-tight" title={r.v.errors.join('\n')} data-testid={`people-import-error-${i}`}>
                                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{r.v.errors[0]}{r.v.errors.length > 1 ? ` (+${r.v.errors.length - 1} more)` : ''}</span>
                              </span>
                            ) : r.isMatch ? (
                              <span className="text-amber-700 flex items-center gap-1 text-[11px]" data-testid={`people-import-status-${i}`}>
                                {duplicateMode === 'update' ? (r.selected ? <><Copy className="w-3 h-3" /> will update</> : <>existing — tick to update</>) : <>existing — skipped</>}
                              </span>
                            ) : (
                              <span className="text-emerald-600 flex items-center gap-1 text-[11px]" data-testid={`people-import-status-${i}`}>
                                <CheckCircle className="w-3 h-3" /> {r.selected ? 'will create' : 'ready'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {step === 3 && (() => {
                const hasLimit = limitInfo && limitInfo.limit >= 0;
                const remaining = hasLimit ? Math.max(0, limitInfo.limit - limitInfo.current) : null;
                const overBy = hasLimit ? counts.create - remaining : 0;
                return (
                  <>
                    {hasLimit && overBy > 0 && (
                      <div className="flex items-start gap-2 rounded-md border border-amber-300/70 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300" data-testid="people-import-limit-warning">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>
                          <b>{meta.label} limit:</b> you have <b>{remaining}</b> slot{remaining === 1 ? '' : 's'} remaining ({limitInfo.current}/{limitInfo.limit} used) but <b>{counts.create}</b> new selected — the last <b>{overBy}</b> will fail unless your limit is increased. Updates to existing {meta.label.toLowerCase()} are not affected.
                        </span>
                      </div>
                    )}
                    <div className="rounded-md border bg-muted/30 p-3 text-sm" data-testid="people-import-dryrun">
                      <p className="font-medium">Dry-run summary</p>
                      <p className="text-muted-foreground text-xs mt-1">
                        <b className="text-emerald-600">{counts.create}</b> will be created · <b className="text-orange-600">{counts.update}</b> updated ·
                        <b className="text-amber-600"> {counts.dupSkipped}</b> existing skipped · <b className="text-red-600">{counts.invalid}</b> blocked by errors
                        {counts.accounts > 0 && <> · <b className="text-blue-600">{counts.accounts}</b> activation invitation(s) will be sent</>}
                        {(counts.beaconAssigns > 0 || counts.cardAssigns > 0) && <> · <b className="text-teal-600">{counts.beaconAssigns}</b> beacon(s) and <b className="text-teal-600">{counts.cardAssigns}</b> RFID card(s) will be assigned</>}. No changes have been made yet.
                        {hasLimit && overBy <= 0 && <> {meta.label} limit: {limitInfo.current + counts.create}/{limitInfo.limit} after import.</>}
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {step === 4 && (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-center">
              {applying ? (
                <><Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" /><p className="text-sm">Importing…</p></>
              ) : results ? (
                <>
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                  <p className="text-lg font-semibold" data-testid="people-import-result">{results.created} created · {results.updated} updated{results.invited ? ` · ${results.invited} invited` : ''}{results.grouped ? ` · ${results.grouped} grouped` : ''}{results.photos ? ` · ${results.photos} photo(s)` : ''}{results.docs ? ` · ${results.docs} document(s)` : ''}{results.beaconsAssigned ? ` · ${results.beaconsAssigned} beacon(s)` : ''}{results.cardsAssigned ? ` · ${results.cardsAssigned} RFID card(s)` : ''}</p>
                  {results.failed.length > 0 && (
                    <div className="text-xs text-red-600 max-h-40 overflow-y-auto w-full max-w-md text-left border rounded-md p-2">
                      {results.failed.map((f, i) => <p key={i}>• {f.name}: {typeof f.error === 'string' ? f.error : 'failed'}</p>)}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t">
          {step === 1 && <Button variant="outline" onClick={close} data-testid="people-import-cancel">Cancel</Button>}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
              <Button onClick={() => setStep(3)} disabled={counts.ready === 0} data-testid="people-import-to-dryrun">Dry-run — {counts.ready} ready <ArrowRight className="w-4 h-4 ml-1" /></Button>
            </>
          )}
          {step === 3 && (
            <>
              <Button variant="outline" onClick={() => setStep(2)}><ArrowLeft className="w-4 h-4 mr-1" /> Back to edit</Button>
              <Button onClick={runImport} disabled={counts.ready === 0} data-testid="people-import-apply"><CheckCircle className="w-4 h-4 mr-1" /> Import {counts.ready} {meta.label}</Button>
            </>
          )}
          {step === 4 && !applying && <Button onClick={close} data-testid="people-import-done">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
